import { Injectable } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { shareReplay } from 'rxjs/operators';

import { setBaseUrl, setVersionCheckUrl, setEnvironmentMqtt, setCardBackupYearsActive, setDemoMode ,
   ENVIRONMENT_MQTT_SERVICE_OPTIONS, setUseDali, setCardWarning, setCardDanger, setRetroMode, setMQTT,
   setResetMqttAtCount,
   setProjectId,
   setVirtualCard,
   setPort,
   setForbidHeatCoolChange,
   setSimplifedRoomStatus,
   setHideStatusFilters,
   setShowCardTrayIgnoredIndicator,
   setFancoilModeShowStatusManual,
   setThrottleCardOnReaderEventsDelay,
   setRetryCardReadOnCardOnHolderEventTimes,
   setVdaCardReaderPort,
   setHideRoomModalHvacTempInfoForRoom,
   setHideRoomModalHvacTempInfoForBathroom,
   setEnableOptionToReplaceCardUidWithPin,
   setShowGenerateQrCodeForCardButton,
   setHystAdjustmentWhenNoValueAvailable
   } from 'src/environments/environment';
import { IMqttServiceOptions, MqttService } from 'ngx-mqtt';
import { StatusFilterConfig } from '../models/status-filters-config.interface';
import { Encoder } from '../models/encoder.model';
import { CardReaderService } from '../services/card-reader.service';
import { EncoderSelectionService } from 'src/app/modules/settings/services/encoder-selection.service';

interface Configuration {
  API_BASE_URL: string;
  VERSION_CHECK_URL: string;
  PORT: string,
  API_URL: string,
  API_BASE: string,
  API_FROM_CONFIG: boolean,
  MQTT_SETTINGS: IMqttServiceOptions;
  BACKUP_CARD_YEARS_ACTIVE: number;
  USE_DALI: boolean,
  CARD_DANGER: number;
  CARD_WARNING: number;
  RETRO_MODE: boolean;
  RESET_MQTT_QUEUE_AT_COUNT: number;
  PROJECT_ID: number;
  DEMO_MODE: boolean;
  VIRTUAL_CARD: boolean;
  FORBID_HEAT_COOL_CHANGE: boolean;
  USE_SIMPLIFIED_ROOM_STATUS: boolean,
  SHOW_CARD_TRAY_IGNORED_INDICATOR: boolean,
  FANCOIL_MODE_SHOW_STATUS_MANUAL: boolean,
  THROTTLE_CARD_ON_READER_EVENTS_DELAY:number,
  RETRY_CARD_READ_ON_CARD_ON_HOLDER_EVENT_TIMES:number,
  VDA_CARD_READER_PORT: number,
  HIDE_ROOM_MODAL_HVAC_TEMP_INFO_FOR_ROOM:boolean,
  HIDE_ROOM_MODAL_HVAC_TEMP_INFO_FOR_BATHROOM:boolean,
  HIDE_STATUS_FILTERS: StatusFilterConfig,
  ENABLE_OPTION_TO_REPLACE_CARD_UID_WITH_PIN: boolean,
  HYST_ADJUSTMENT_WHEN_NO_VALUE_AVAILABLE : {
    roomHyst1: number,
    roomHyst2: number,
    bathroomHyst1: number,
    bathroomHyst2: number
  },
  HIDE_GENERATE_QR_CODE_FOR_CARD: boolean
}

@Injectable({
  providedIn: 'root'
})
export class ConfigLoaderService {

  private readonly CONFIG_URL = 'assets/config/config.json';
  private configuration$: Observable<Configuration>;

  constructor(
    private http: HttpClient,
    private mqttService: MqttService,
    private cardReaderService: CardReaderService,
    private encoderSelectionService: EncoderSelectionService
              ) {
  }

  public loadConfiguration(): any {
    if (!this.configuration$) {
      this.configuration$ = this.http.get<Configuration>(this.CONFIG_URL).pipe(
        shareReplay(1)
      );
      this.configuration$.subscribe((config: Configuration) => {
        setEnvironmentMqtt(config.MQTT_SETTINGS)
        if (config.API_FROM_CONFIG) {
          if (config.DEMO_MODE) {
            setBaseUrl(config.PORT, config.API_URL, '');
          } else {
            setBaseUrl(config.PORT, config.API_URL, config.API_BASE);
          }
        } else {
          let url = window.location.href.split('/')[0] + '//' + window.location.href.split('/')[2]
          url = url.split(":", 2).join(":")
          const mqttUrl = url.split('/')
          if (config.DEMO_MODE) {
            setBaseUrl(config.PORT, url, '')
          } else {
            setBaseUrl(config.PORT, url, config.API_BASE)
          }
          setMQTT(mqttUrl[0], mqttUrl[2])
        }
        setVersionCheckUrl(config.VERSION_CHECK_URL);
        setCardBackupYearsActive(config.BACKUP_CARD_YEARS_ACTIVE);
        setUseDali(config.USE_DALI)
        setCardWarning(config.CARD_WARNING)
        setCardDanger(config.CARD_DANGER)
        setRetroMode(config.RETRO_MODE)
        setResetMqttAtCount(config.RESET_MQTT_QUEUE_AT_COUNT)
        setProjectId(config.PROJECT_ID)
        setDemoMode(config.DEMO_MODE)
        setVirtualCard(config.VIRTUAL_CARD)
        setPort(config.PORT)
        setForbidHeatCoolChange(config.FORBID_HEAT_COOL_CHANGE)
        setSimplifedRoomStatus(config.USE_SIMPLIFIED_ROOM_STATUS)
        setHideStatusFilters(config.HIDE_STATUS_FILTERS)
        setShowCardTrayIgnoredIndicator(config.SHOW_CARD_TRAY_IGNORED_INDICATOR)
        setFancoilModeShowStatusManual(config.FANCOIL_MODE_SHOW_STATUS_MANUAL)
        setThrottleCardOnReaderEventsDelay(config.THROTTLE_CARD_ON_READER_EVENTS_DELAY)
        setRetryCardReadOnCardOnHolderEventTimes(config.RETRY_CARD_READ_ON_CARD_ON_HOLDER_EVENT_TIMES)
        setVdaCardReaderPort(config.VDA_CARD_READER_PORT);
        setHideRoomModalHvacTempInfoForRoom(config.HIDE_ROOM_MODAL_HVAC_TEMP_INFO_FOR_ROOM);
        setHideRoomModalHvacTempInfoForBathroom(config.HIDE_ROOM_MODAL_HVAC_TEMP_INFO_FOR_BATHROOM);
        setEnableOptionToReplaceCardUidWithPin(config.ENABLE_OPTION_TO_REPLACE_CARD_UID_WITH_PIN);
        setHystAdjustmentWhenNoValueAvailable(config.HYST_ADJUSTMENT_WHEN_NO_VALUE_AVAILABLE);
        setShowGenerateQrCodeForCardButton(config.HIDE_GENERATE_QR_CODE_FOR_CARD);

        if (!config.DEMO_MODE) {
          this.mqttService.connect(ENVIRONMENT_MQTT_SERVICE_OPTIONS)
          const storedEncoder: Encoder = Encoder.getEncoderDataFromStorage();
          if (storedEncoder && !Encoder.selectionIsIgnored(storedEncoder)) {
            this.encoderSelectionService.selectEncoder(storedEncoder);
            this.cardReaderService.initialize(storedEncoder);
          }
        }
      });
    }
    return lastValueFrom(this.configuration$);
  }
}
